import { Field, SelectField } from '@today/ui-components'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { Option, Translate } from './customer-types'

const REASONS = ['manual_reassign', 'user_offboarded', 'unit_restructure']

export interface ReassignDialogViewProps {
	t: Translate
	isOpen: boolean
	orgUnits: Option[]
	users: Option[]
	values: { orgUnitId: string; userId: string; reason: string }
	error: string | null
	onChange: (field: 'orgUnitId' | 'userId' | 'reason', value: string) => void
	onConfirm: () => void
	onClose: () => void
}

/** Reassign dialog: unit + user + reason; server errors stay inside the open dialog (organism). */
export function ReassignDialogView(props: ReassignDialogViewProps): ReactElement {
	const { t } = props
	const pick = (field: 'orgUnitId' | 'userId', label: string, options: Option[]) => (
		<SelectField
			label={t(label)}
			value={props.values[field]}
			placeholder={t('profile.none')}
			onValueChange={(v) => props.onChange(field, v)}
			options={options.map((o) => ({ id: o.id, text: o.label }))}
		/>
	)
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onClose())}
			title={t('dialogs.reassignTitle')}
		>
			<div className="flex flex-col gap-3">
				{pick('orgUnitId', 'assignment.orgUnit', props.orgUnits)}
				{pick('userId', 'assignment.user', props.users)}
				<SelectField
					label={t('assignment.reason')}
					value={props.values.reason}
					onValueChange={(v) => props.onChange('reason', v)}
					options={REASONS.map((id) => ({ id, text: t(`assignment.reasons.${id}`) }))}
				/>
				{props.error ? (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{props.error}
					</p>
				) : null}
				<Button onPress={props.onConfirm}>{t('actions.confirm')}</Button>
			</div>
		</Dialog>
	)
}

/** Convert-to-customer confirmation (organism). */
export function ConvertDialogView({
	t,
	isOpen,
	onConfirm,
	onClose,
}: {
	t: Translate
	isOpen: boolean
	onConfirm: () => void
	onClose: () => void
}): ReactElement {
	return (
		<Dialog isOpen={isOpen} onOpenChange={(open) => (open ? undefined : onClose())} title={t('dialogs.convertTitle')}>
			<p className="text-ds-sm text-ds-text">{t('dialogs.convertBody')}</p>
			<Button onPress={onConfirm}>{t('actions.confirm')}</Button>
		</Dialog>
	)
}

export interface AnonymizeDialogViewProps {
	t: Translate
	isOpen: boolean
	fullName: string
	typed: string
	onTypedChange: (value: string) => void
	onConfirm: () => void
	onClose: () => void
}

/** Anonymise confirmation: enabled only when the exact full name is typed (organism). */
export function AnonymizeDialogView(props: AnonymizeDialogViewProps): ReactElement {
	const { t } = props
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onClose())}
			title={t('dialogs.anonymizeTitle')}
		>
			<p className="text-ds-sm text-ds-text">{t('dialogs.anonymizeBody')}</p>
			<Field
				label={t('dialogs.anonymizePrompt', { name: props.fullName })}
				value={props.typed}
				onChange={(e) => props.onTypedChange(e.target.value)}
			/>
			<Button variant="danger" isDisabled={props.typed !== props.fullName} onPress={props.onConfirm}>
				{t('actions.anonymize')}
			</Button>
		</Dialog>
	)
}
